import { createContext, Dispatch, FC, useContext, useMemo } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { Children } from '../constants/types/prop.types.ts';

export type CaptchaOptions = {
  failedCount: number;
  failedV3: boolean;
};

export type CaptchaControlType = Record<string, CaptchaOptions>;
interface ICaptchaControlContext {
  increaseRequestFailedCount: Dispatch<string>;
  setFailedV3: Dispatch<string>;
  resetRequestController: Dispatch<string>;
  failedCaptchaCounts: CaptchaControlType;
}

const CaptchaControlContext = createContext<ICaptchaControlContext>({
  increaseRequestFailedCount: () => {},
  setFailedV3: () => {},
  resetRequestController: () => {},
  failedCaptchaCounts: {},
});

const CaptchaControlProvider: FC<Children> = ({ children }) => {
  const [failedCounts, setFailedCounts] = useLocalStorage<CaptchaControlType>(
    'failed-captcha-count',
    {},
  );

  const increaseRequestFailedCount = (requestPath: string) => {
    if (!failedCounts.hasOwnProperty(requestPath)) {
      resetRequestController(requestPath);
    }

    return setFailedCounts({
      ...failedCounts,
      [requestPath]: {
        ...failedCounts[requestPath],
        failedCount: (failedCounts[requestPath]?.failedCount || 0) + 1,
      },
    });
  };
  const setFailedV3 = (requestPath: string) => {
    if (!failedCounts.hasOwnProperty(requestPath)) {
      resetRequestController(requestPath);
    }

    setFailedCounts({
      ...failedCounts,
      [requestPath]: {
        failedCount: !failedCounts[requestPath]?.failedCount
          ? 1
          : failedCounts[requestPath]?.failedCount,
        failedV3: true,
      },
    });
  };

  const resetRequestController = (requestPath: string) => {
    setFailedCounts({
      ...failedCounts,
      [requestPath]: {
        failedCount: 0,
        failedV3: false,
      },
    });
  };

  const value = useMemo(
    () => ({
      increaseRequestFailedCount,
      setFailedV3,
      resetRequestController,
      failedCaptchaCounts: failedCounts,
    }),
    [increaseRequestFailedCount, setFailedV3, resetRequestController, failedCounts],
  );

  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    <CaptchaControlContext.Provider value={value}>{children}</CaptchaControlContext.Provider>
  );
};

export default CaptchaControlProvider;

export const useCaptchaControl = () => useContext(CaptchaControlContext);
